import { Modal, Typography } from 'antd';
import { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import PublishCatalogStepper from '../PublishCatalogStepper';
import { getDraftedCatalogue } from '../../../services/api/draftedCatalogueService';
import { decryptCataloguePassword } from '../../../services/utils/cataloguePasswordUtils';

const PublishModal = ({ modalState, setModalState, fetchCatalogues, messageApi, statusTokens, stepperKey, currentStep, setCurrentStep, publishDraftId, setPublishDraftId }) => {
    const [draftData, setDraftData] = useState(null);
    const [loadingDraft, setLoadingDraft] = useState(false);

    const handleModalClose = () => {
        setModalState('closed');
        if (setPublishDraftId) {
            setPublishDraftId(null);
        }
        // Reset draft data when closing
        setDraftData(null);
    };

    // Load draft data when publishDraftId changes
    useEffect(() => {
        const loadDraftData = async () => {
            if (publishDraftId && modalState === 'publish') {
                setLoadingDraft(true);
                try {
                    const data = await getDraftedCatalogue(publishDraftId);

                    if (data) {
                        // Map database structure to form structure
                        const mappedData = {
                            licenseList: data.licenseList || '',
                            catalogueName: data.nameCatalog || '',
                            logo: data.urlLogo || null,
                            domain: data.webDomain || '',
                            webUrl: data.webUrl || '', // Target Domain field
                            accessMethod: data.accessMethod || '',
                            licenseRelationship: data.licenseRelationship || '',
                            permissions: {
                                public: data.isPublic !== undefined ? data.isPublic : true,
                                requireLogin: data.isLoginRequired !== undefined ? data.isLoginRequired : false,
                                loginPassword: (() => {
                                    // Decrypt the password to display it in the form
                                    if (data.loginPassword) {
                                        try {
                                            return decryptCataloguePassword(data.loginPassword);
                                        } catch (error) {
                                            console.error('Error decrypting password in publish modal:', error);
                                            return '';
                                        }
                                    }
                                    return '';
                                })(),
                                iframeOnlyAccess: data.isIframeOnlyAcces !== undefined ? data.isIframeOnlyAcces : false,
                                allowedDomain: data.allowedDomain || ''
                            },
                            seo: {
                                title: data.titleSeo || '',
                                featuredImage: data.featuredImage || null,
                                metaDescriptions: {
                                    it: data.descriptionIt || '',
                                    en: data.descriptionEn || '',
                                    fr: data.descriptionFr || '',
                                    es: data.descriptionEs || '',
                                    de: data.descriptionDe || ''
                                },
                                keywords: data.keywords || data.list_keywords || []
                            },
                            customization: {
                                primaryColor: data.catalogues_page_style?.primaryColor || '#46518a',
                                secondaryColor: data.catalogues_page_style?.secondaryColor || '#929ACC',
                                backgroundColor: data.catalogues_page_style?.backgroundColor || '#FFFFFF',
                                bannerBackgroundColor: data.catalogues_page_style?.bannerBackgroundColor,
                                cardCornerTopLeft: data.catalogues_page_style?.cardCornerTopLeft || 0,
                                cardCornerTopRight: data.catalogues_page_style?.cardCornerTopRight || 0,
                                cardCornerBottomRight: data.catalogues_page_style?.cardCornerBottomRight || 0,
                                cardCornerBottomLeft: data.catalogues_page_style?.cardCornerBottomLeft || 0,
                                buttonCornerTopLeft: data.catalogues_page_style?.buttonCornerTopLeft || 0,
                                buttonCornerTopRight: data.catalogues_page_style?.buttonCornerTopRight || 0,
                                buttonCornerBottomRight: data.catalogues_page_style?.buttonCornerBottomRight || 0,
                                buttonCornerBottomLeft: data.catalogues_page_style?.buttonCornerBottomLeft || 0,
                                bannerCornerTopLeft: data.catalogues_page_style?.bannerCornerTopLeft || 0,
                                bannerCornerTopRight: data.catalogues_page_style?.bannerCornerTopRight || 0,
                                bannerCornerBottomRight: data.catalogues_page_style?.bannerCornerBottomRight || 0,
                                bannerCornerBottomLeft: data.catalogues_page_style?.bannerCornerBottomLeft || 0
                            },
                            banner: {
                                heightOption: data.catalogues_page_style?.heightOption || '100',
                                height: data.catalogues_page_style?.coverHeightDesktop || 100,
                                heightMobileOption: data.catalogues_page_style?.heightMobileOption || '100',
                                heightMobile: data.catalogues_page_style?.coverHeightMobile || 100,
                                bannerImage: data.catalogues_page_style?.coverImg || null,
                                bannerImageMobile: data.catalogues_page_style?.coverImgMobile || null,
                                logoPosition: data.catalogues_page_style?.catalogueLogoPosition || 'center',
                                isLogoVisible: data.catalogues_page_style?.isLogoVisible !== undefined ? data.catalogues_page_style.isLogoVisible : true
                            }
                        };
                        setDraftData(mappedData);
                    }
                } catch (error) {
                    console.error('Errore nel caricamento dei dati del draft:', error);
                    messageApi.error('Errore nel caricamento dei dati del draft');
                } finally {
                    setLoadingDraft(false);
                }
            } else {
                setDraftData(null);
            }
        };

        loadDraftData();
    }, [publishDraftId, modalState, messageApi]);

    return (
        <Modal
            title={
                <Typography.Title level={4} style={{ marginBottom: '1rem' }}>
                    {publishDraftId ? 'Publish Draft' : 'Publish Catalogue'}
                </Typography.Title>
            }
            open={modalState === 'publish'}
            onCancel={handleModalClose}
            width="85vw"
            centered
            maskClosable={false}
            footer={null}
            destroyOnHidden={true}
            closeIcon={
                <div
                    className="modal-close-button"
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '40px',
                        height: '40px',
                        borderRadius: '6px',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        backgroundColor: 'transparent',
                        padding: '8px',
                    }}
                    onClick={handleModalClose}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                >
                    <CloseIcon
                        style={{
                            fontSize: '24px',
                            color: '#666',
                            margin: 0,
                            pointerEvents: 'none'
                        }}
                    />
                </div>
            }
        >
            <PublishCatalogStepper
                key={stepperKey}
                onCancel={handleModalClose}
                onFinish={() => {
                    messageApi.success(publishDraftId ? 'Draft published successfully' : 'Catalogue published successfully');
                    handleModalClose();
                    fetchCatalogues(true); // Force refresh after publishing
                }}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                initialFormData={publishDraftId ? draftData : null}
                draftId={publishDraftId}
                onRefreshCatalogues={() => fetchCatalogues(true)}
            />
        </Modal>
    )
}

export default PublishModal;